import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { Building2, Sun, Moon, Globe } from 'lucide-react';
import { toast } from 'sonner';

export const LoginPage = () => {
  const [orgCode, setOrgCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t, toggleLanguage, language } = useLanguage();
  const { theme, toggleTheme } = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error(t('error'), { description: language === 'tr' ? 'Kullanıcı adı ve şifre gerekli' : 'Username and password required' });
      return;
    }
    setLoading(true);
    try {
      await login(username, password, orgCode);
      toast.success(t('success'), { description: language === 'tr' ? 'Giriş başarılı' : 'Login successful' });
    } catch (error) {
      toast.error(t('error'), { description: error.response?.data?.detail || (language === 'tr' ? 'Geçersiz bilgiler' : 'Invalid credentials') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-secondary/30 to-background">
      <div className="absolute top-4 right-4 flex gap-2">
        <button onClick={toggleTheme} className="p-3 rounded-lg bg-card border border-border hover:bg-muted transition-colors" data-testid="theme-toggle">
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>
        <button onClick={toggleLanguage} className="p-3 rounded-lg bg-card border border-border hover:bg-muted transition-colors flex items-center gap-2" data-testid="language-toggle">
          <Globe className="w-5 h-5" />
          <span className="text-sm font-medium">{language === 'tr' ? 'EN' : 'TR'}</span>
        </button>
      </div>

      <div className="w-full max-w-md animate-fade-in">
        <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
          <div className="text-center mb-8">
            <img src="/flowupy-logo.png" alt="FlowUpy" className="w-16 h-16 object-contain mx-auto mb-4" />
            <h1 className="text-3xl font-bold mb-2">{t('appName')}</h1>
            <p className="text-muted-foreground">{t('loginSubtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">{language === 'tr' ? 'Otel Kodu' : 'Hotel Code'}</label>
              <input
                type="text"
                value={orgCode}
                onChange={(e) => setOrgCode(e.target.value.toUpperCase())}
                className="input-field uppercase tracking-widest"
                placeholder={language === 'tr' ? 'Örn: DEMO01' : 'e.g. DEMO01'}
                data-testid="login-org-code"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{t('username')}</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="input-field"
                data-testid="login-username"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{t('password')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field"
                data-testid="login-password"
              />
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full py-3 rounded-lg disabled:opacity-50" data-testid="login-submit">
              {loading ? t('loading') : t('login')}
            </button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            {language === 'tr' ? 'Hesabınız yok mu?' : 'No account?'}{' '}
            <Link to="/kayit" className="text-primary font-semibold hover:underline">{language === 'tr' ? 'Ücretsiz Başlayın' : 'Get Started'}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};
